import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select';

export default class extends Controller {
  static values = {
    url: { type: String, default: null },
    options: { type: Array, default: [] },
    recordType: { type: String, default: "Company" },
    selected: { type: Array },
    singleItem: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select an option" },
    companyId: Number,
    groupId: Number,
    autoFocus: { type: Boolean, default: false },
    assetType: { type: String, default: null },
    itemsOutside: { type: Boolean, default: false },
    recordTypeFilter: { type: String, default: null },
    allOption: String,
    preserveOrder: { type: Boolean, default: false },
    autoSubmit: { type: Boolean, default: false },
    requiredField: { type: Boolean, default: false },
    prependOptions: { type: Array, default: [] },
    truncated: { type: Boolean, default: false },
    showSelectAll: { type: Boolean, default: true }
  };

  // Submitted as the value when the "All" option is selected.
  static allOptionId = "__all__"
  static targets = ["select", "companyButton", "globalButton", "itemsContainer"];

  connect() {
    this.prependIdsSet = new Set(this.prependOptionsValue.map(o => String(o.id)));
    this.searchGlobal = false;
    this.isInitialized = false;
    this.isInitializing = true;
    this.initializeTomSelect();
    this.isInitializing = false;
  }

  requiredFieldValueChanged(value) {
    if (value) {
      this.setupRequiredFieldValidation();
    } else {
      this.cleanupRequiredFieldValidation();
    }
  }

  disconnect() {
    this.cancelPostTurboClose();
    this.destroyTomSelect();
    this.cleanupRequiredFieldValidation();
  }

  setupRequiredFieldValidation() {
    if (!this.requiredFieldValue) return;

    this.parentForm = this.element.closest('form');
    if (!this.parentForm) return;

    this.boundValidateRequiredField = this.validateRequiredField.bind(this);
    this.parentForm.addEventListener('submit', this.boundValidateRequiredField);

    if (this.hasSelectTarget) {
      this.boundClearRequiredFieldValidity = this.clearRequiredFieldValidity.bind(this);
      this.selectTarget.addEventListener('change', this.boundClearRequiredFieldValidity);
    }
  }

  cleanupRequiredFieldValidation() {
    if (this.parentForm && this.boundValidateRequiredField) {
      this.parentForm.removeEventListener('submit', this.boundValidateRequiredField);
    }
    if (this.hasSelectTarget && this.boundClearRequiredFieldValidity) {
      this.selectTarget.removeEventListener('change', this.boundClearRequiredFieldValidity);
    }
    this.removeRequiredFieldError();
  }

  validateRequiredField(event) {
    if (!this.select) return;
    if (this.select.items && this.select.items.length > 0) return;

    event.preventDefault();
    // Keeps @rails/ujs's document-level submit handler from disabling the
    // submit button on a cancelled submission.
    event.stopPropagation();

    this.showRequiredFieldError();
  }

  // The underlying <select> is hidden by TomSelect, and the browser's native
  // validation bubble (reportValidity) is mispositioned and does not follow
  // scroll in Firefox (Bugzilla 1041529 / 1616794), stranding the message far
  // from the field on long forms. Render a plain inline error under the visible
  // control instead: it cannot be clipped, hidden behind the dropdown, or
  // dismissed by the click that triggered it, and it stays with the field.
  showRequiredFieldError() {
    if (this.select.wrapper) {
      this.select.wrapper.classList.add('ts-wrapper--error');
    }

    if (!this.requiredFieldError) {
      this.requiredFieldError = document.createElement('div');
      this.requiredFieldError.className = 'select-required-error';
      this.requiredFieldError.setAttribute('role', 'alert');
      this.requiredFieldError.textContent = 'Please select an option.';
      this.element.appendChild(this.requiredFieldError);
    }

    const scrollTarget = this.select.wrapper || this.element;
    if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
      scrollTarget.scrollIntoView({ block: 'center' });
    }
  }

  clearRequiredFieldValidity() {
    if (this.select && this.select.wrapper) {
      this.select.wrapper.classList.remove('ts-wrapper--error');
    }
    this.removeRequiredFieldError();
  }

  removeRequiredFieldError() {
    if (this.requiredFieldError) {
      this.requiredFieldError.remove();
      this.requiredFieldError = null;
    }
  }

  // Only allow alphanumeric, hyphens, underscores, and spaces (for multiple classes)
  sanitizeIconClass(iconClass) {
    if (!iconClass) return '';
    return String(iconClass).replace(/[^a-zA-Z0-9\s_-]/g, '');
  }

  // Sanitize color values before interpolating into inline styles
  sanitizeColor(color) {
    if (!color) return null;
    return /^#[0-9a-fA-F]{3,6}$/.test(color) ? color : null;
  }

  changeToGlobal(event) {
    event.preventDefault();
    this.select.clearOptions();
    this.select.setTextboxValue('');

    this.searchGlobal = true;
    this.companyButtonTarget.classList.remove("selected");
    this.globalButtonTarget.classList.add("selected");

    this.select.trigger("load");
  }

  changeToCompany(event) {
    event.preventDefault();
    this.select.clearOptions();
    this.select.setTextboxValue('');

    this.searchGlobal = false;
    this.companyButtonTarget.classList.add("selected");
    this.globalButtonTarget.classList.remove("selected");

    this.select.trigger("load");
  }

  initializeTomSelect() {
    if (!this.element) return;

    const fetchData = this.urlValue ? this.fetchFromUrl : this.fetchFromOptions;

    const renderOption = (data, escape) => {
      let avatarHtml = '';
      let subtextHtml = '';

      if (data.avatar_url) {
        avatarHtml = `<img src="${escape(data.avatar_url)}" class="select-avatar" />`;
      } else if (data.color) {
        const safeColor = this.sanitizeColor(data.color);
        if (safeColor) {
          avatarHtml = `<div class='circle circle--flag circle--table' style="background-color: ${safeColor};"></div>`;
        }
      } else if (data.icon) {
        avatarHtml = `<div class='asset-icon' style='color: ${escape(data.icon_color)}; background-color: ${escape(data.icon_background_color)};'>
          <i class='${this.sanitizeIconClass(data.icon)}'></i>
        </div>`;
      }

      if (data.subtext) {
        subtextHtml = `<span class='index__small'>${escape(data.subtext)}</span>`;
      } else if (data.company_name && data.asset_type) {
        subtextHtml = `<span class='index__small'>${escape(data.asset_type)} / ${escape(data.company_name)}</span>`;
      }

      return `
        <div style="display: flex; align-items: center;">
          ${avatarHtml}
          <div>
            <span>${escape(data.name)}</span>
            ${subtextHtml}
          </div>
        </div>
      `;
    };

    // items_outside: use no_backspace_delete so backspace doesn't silently remove items
    // when the search field is empty; each external item has its own remove button.
    let plugins = [];
    if (!this.singleItemValue && !this.truncatedValue) {
      plugins = this.itemsOutsideValue ? ['no_backspace_delete'] : ['remove_button'];
    }

    const tomSelectOptions = {
      plugins: plugins,
      valueField: 'id',
      labelField: 'name',
      searchField: ['name'],
      maxItems: this.singleItemValue ? 1 : null,
      placeholder: this.placeholderValue,
      preload: true,
      create: false,
      load: fetchData,
      hideSelected: !this.truncatedValue,
      closeAfterSelect: !this.truncatedValue,
      onLoad: () => {
        this.updateAllSelectedState();
      },
      onFocus: () => {
        if (this.isInitialized) {
          this.updateAllSelectedState();
        }
      },
      onItemAdd: (value) => {
        this.enforceExclusiveAllOption(value);
        this.handleItemChange(value);
      },
      onItemRemove: () => this.handleItemChange(),
      onChange: (value) => {
        if (!this.isInitializing) {
          this.updateAllSelectedState();
        }
        // Auto-submit lives here so programmatic changes (setValue, clear, server updates)
        // also trigger a submit, not just user item-add/remove events.
        if (!this.isInitializing && this.autoSubmitValue) {
          this.syncSelectValue(value);
          this.submitForm();
        }
      },
      render: {
        item: (data, escape) => {
          if (this.truncatedValue || (this.itemsOutsideValue && !this.singleItemValue)) {
            return '<div style="display: none;"></div>';
          }

          // Check for color in data first (for URL-based searches), then in optionsValue (for static options)
          const option = this.optionsValue.find(opt => opt.id == data.id) || {};
          const color = data.color || option.color;
          const hideRemove = data.hide_remove || option.hide_remove || false;

          if (color) {
            const safeColor = this.sanitizeColor(color);
            return `
              <div data-hide-remove="${hideRemove}" style="display: flex; align-items: center;">
                ${safeColor ? `<div class='circle circle--flag circle--table' style="background-color: ${safeColor};"></div>` : ''}
                ${escape(data.name)}
              </div>
            `;
          } else {
            const hasIconData = data.icon || data.icon_color || data.icon_background_color;
            const iconHtml = hasIconData ? `<div class='asset-icon small' style='color: ${escape(data.icon_color)}; background-color: ${escape(data.icon_background_color)};'>
                <i class='${this.sanitizeIconClass(data.icon)}'></i>
               </div>` : '';

            return `
              <div data-hide-remove="${hideRemove}" style="display: flex; align-items: center;">
                ${iconHtml}
                <span>${escape(data.name)}</span>
              </div>
            `;
          }
        },
        option: renderOption,
        no_results: () => {
          return '<div class="no-results">No results found</div>';
        }
      },
    };

    // Only apply sorting when preserveOrder is false.
    // Omitting sortField entirely lets TomSelect preserve insertion order.
    // Rank fields pin options to the top before the alphabetical name sort:
    // all_option_rank for the synthetic "All" option and prepend_rank for
    // explicitly prepended options. Each is only included when its feature is
    // active so the features can coexist.
    if (!this.preserveOrderValue) {
      const sortFields = [];
      if (this.hasAllOptionValue) sortFields.push({ field: "all_option_rank", direction: "asc" });
      if (this.prependOptionsValue.length > 0) sortFields.push({ field: "prepend_rank", direction: "asc" });
      sortFields.push({ field: "name", direction: "asc" });
      tomSelectOptions.sortField = sortFields.length === 1 ? sortFields[0] : sortFields;
    }

    this.select = new TomSelect(this.selectTarget, tomSelectOptions);

    if (this.selectedValue.length > 0) {
      this.selectedValue.forEach((value) => {
        this.select.addOption(this.decorateOption(value));
        this.select.addItem(value.id);
      });
    }

    if (this.truncatedValue) {
      this.initTruncatedBehavior();
    }

    if (this.itemsOutsideValue && !this.singleItemValue) {
      this.renderExternalItems();
    }

    if (this.autoFocusValue) {
      setTimeout(() => {
        this.select.focus();
      }, 100);
    }

    // Mark as initialized after all initial items are added
    setTimeout(() => {
      this.isInitialized = true;
    }, 0);

    // After a Turbo frame replacement, the browser may restore focus to the
    // newly created input, causing the dropdown to reopen. Ensure it starts closed.
    if (this.itemsOutsideValue && this.autoSubmitValue) {
      this.postTurboCloseRaf = requestAnimationFrame(() => {
        this.postTurboCloseRaf = null;
        if (this.select) {
          this.select.close();
          this.select.blur();
        }
      });
    }
  }

  enforceExclusiveAllOption(addedValue) {
    if (this.singleItemValue || !this.hasAllOptionValue || !this.select) {
      return;
    }

    const allId = this.constructor.allOptionId;
    const addedItemId = String(addedValue);
    const selectedItems = this.select.items.map(id => String(id));

    if (addedItemId === allId) {
      this.select.items
        .filter(id => String(id) !== allId)
        .forEach(id => this.select.removeItem(id, true));
      return;
    }

    if (selectedItems.includes(allId)) {
      this.select.removeItem(allId, true);
    }
  }

  handleItemChange(value) {
    this.select.setTextboxValue('');
    if (this.truncatedValue) {
      this.select.refreshOptions(false);
      this.renderTruncatedChips();
      this.updateSelectAllState();
      return;
    }
    if (this.itemsOutsideValue && !this.singleItemValue) {
      this.renderExternalItems();
    }
    this.select.close();
  }

  initTruncatedBehavior() {
    this.select.wrapper.classList.add('truncated-select');

    // Override TomSelect internal: onOptionSelect. We need click-on-option to toggle
    // (deselect if already selected) and keep the dropdown open. TomSelect has no
    // public API for this. Re-check after any TomSelect upgrade—method name/signature
    // may change.
    const original = this.select.onOptionSelect.bind(this.select);
    this.select.onOptionSelect = (evt, option) => {
      const value = option.dataset.value;
      if (this.select.items.includes(String(value))) {
        this.select.removeItem(value);
        return;
      }
      original(evt, option);
    };

    if (this.showSelectAllValue) {
      this.injectSelectAll();
    }

    this.renderTruncatedChips();
    this.updateSelectAllState();

    this.select.on('load', () => this.updateSelectAllState());
  }

  injectSelectAll() {
    this.selectAllWrapper = document.createElement('div');
    this.selectAllWrapper.classList.add('ts-select-all', 'option');

    this.selectAllCheckbox = document.createElement('input');
    this.selectAllCheckbox.type = 'checkbox';
    this.selectAllCheckbox.setAttribute('tabindex', '-1');

    const text = document.createElement('span');
    text.textContent = 'Select all';

    this.selectAllWrapper.appendChild(this.selectAllCheckbox);
    this.selectAllWrapper.appendChild(text);
    this.selectAllWrapper.setAttribute('role', 'option');
    this.selectAllWrapper.setAttribute('data-action', 'mousedown->select#preventBlur click->select#toggleSelectAll');

    const dropdown = this.select.dropdown;
    dropdown.insertBefore(this.selectAllWrapper, dropdown.firstChild);

    // Override TomSelect internal: onKeyDown. The "Select all" row is custom DOM, not
    // a real option, so we intercept keydown to handle ArrowUp/Down and Enter/Space
    // for that row. Re-check after any TomSelect upgrade—method name/signature may
    // change.
    const originalKeyDown = this.select.onKeyDown.bind(this.select);
    this.select.onKeyDown = (e) => {
      if (!this.select.isOpen) return originalKeyDown(e);

      const selectAllActive = this.select.activeOption === this.selectAllWrapper;

      if (e.key === 'ArrowUp') {
        const firstOption = this.select.dropdown_content.querySelector('[data-selectable]:first-child');
        if (this.select.activeOption === firstOption || !this.select.activeOption) {
          e.preventDefault();
          this.select.setActiveOption(this.selectAllWrapper, false);
          return;
        }
      }

      if (e.key === 'ArrowDown' && selectAllActive) {
        e.preventDefault();
        const firstOption = this.select.dropdown_content.querySelector('[data-selectable]');
        if (firstOption) this.select.setActiveOption(firstOption);
        return;
      }

      if ((e.key === 'Enter' || e.key === ' ') && selectAllActive) {
        e.preventDefault();
        this.toggleSelectAll();
        return;
      }

      if (selectAllActive) return;

      originalKeyDown(e);
    };
  }

  preventBlur(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  toggleSelectAll() {
    this.selectAllCheckbox.checked = !this.selectAllCheckbox.checked;
    const allValues = Object.keys(this.select.options);
    if (this.selectAllCheckbox.checked) {
      allValues.forEach(v => this.select.addItem(v, true));
    } else {
      allValues.forEach(v => this.select.removeItem(v, true));
    }
    this.select.refreshOptions(false);
    this.renderTruncatedChips();
    this.updateSelectAllState();
  }

  updateSelectAllState() {
    if (!this.selectAllCheckbox) return;

    const totalCount = Object.keys(this.select.options).length;
    const selectedCount = this.select.items.length;

    this.selectAllCheckbox.checked = (selectedCount === totalCount && totalCount > 0);
  }

  renderTruncatedChips() {
    const control = this.select.control;
    const input = control.querySelector('input');
    control.querySelectorAll('.truncated-chip').forEach(el => el.remove());

    const items = this.select.items;
    if (items.length === 0) return;

    const visibleCount = items.length <= 2 ? items.length : 1;

    for (let i = 0; i < visibleCount; i++) {
      const itemId = items[i];
      const option = this.select.options[itemId];
      if (!option) continue;
      const chip = document.createElement('div');
      chip.classList.add('truncated-chip');

      const iconEl = this.buildChipIcon(option);
      if (iconEl) {
        iconEl.classList.add('truncated-chip__icon');
        chip.appendChild(iconEl);
      }

      const label = document.createElement('span');
      label.classList.add('truncated-chip__label');
      label.textContent = option.name;
      chip.appendChild(label);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.classList.add('truncated-chip__remove', 'remove-item');
      removeBtn.setAttribute('aria-label', 'Remove');
      removeBtn.dataset.itemId = itemId;
      removeBtn.dataset.action = 'click->select#removeTruncatedItem';
      chip.appendChild(removeBtn);
      control.insertBefore(chip, input);
    }

    const remaining = items.length - visibleCount;
    if (remaining > 0) {
      const moreChip = document.createElement('div');
      moreChip.classList.add('truncated-chip', 'truncated-chip--more');
      moreChip.textContent = `+ ${remaining} more`;
      control.insertBefore(moreChip, input);
    }
  }

  buildChipIcon(option) {
    if (option.avatar_url) {
      const img = document.createElement('img');
      img.src = option.avatar_url;
      img.alt = '';
      img.classList.add('select-avatar');
      return img;
    }
    if (option.color) {
      const safeColor = this.sanitizeColor(option.color);
      const circle = document.createElement('div');
      if (safeColor) {
        circle.className = 'circle circle--flag circle--table';
        circle.style.backgroundColor = safeColor;
      } else {
        const safeClass = this.sanitizeIconClass(option.color);
        if (!safeClass) return null;
        circle.className = `circle circle--flag circle--table circle--${safeClass}`;
      }
      return circle;
    }
    if (option.icon || option.icon_color || option.icon_background_color) {
      const wrap = document.createElement('div');
      wrap.className = 'asset-icon small';
      wrap.style.color = option.icon_color || '';
      wrap.style.backgroundColor = option.icon_background_color || '';
      const i = document.createElement('i');
      i.className = this.sanitizeIconClass(option.icon);
      wrap.appendChild(i);
      return wrap;
    }
    return null;
  }

  removeTruncatedItem(event) {
    event.preventDefault();
    const itemId = event.currentTarget.dataset.itemId;
    if (itemId) {
      this.select.removeItem(itemId);
    }
  }

  renderExternalItems() {
    if (!this.hasItemsContainerTarget) return;

    this.itemsContainerTarget.replaceChildren();

    this.select.items.forEach(itemId => {
      const option = this.select.options[itemId];
      if (!option) return;

      const optionData = this.optionsValue.find(opt => opt.id == itemId) || {};
      const item = document.createElement('div');
      item.className = 'external-item';

      // Check for color in option data first (URL-based searches), then in optionsValue (static options)
      const color = option.color || optionData.color;
      if (color) {
        const safeColor = this.sanitizeColor(color);
        if (safeColor) {
          const circle = document.createElement('div');
          circle.className = 'circle circle--flag circle--table';
          circle.style.backgroundColor = safeColor;
          circle.style.marginRight = '6px';
          item.appendChild(circle);
        } else {
          const safeClass = this.sanitizeIconClass(color);
          if (safeClass) {
            const circle = document.createElement('div');
            circle.className = `circle circle--flag circle--table circle--${safeClass}`;
            circle.style.marginRight = '6px';
            item.appendChild(circle);
          }
        }
      }

      const label = document.createElement('span');
      label.textContent = option.name;
      item.appendChild(label);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'remove-item';
      removeBtn.setAttribute('aria-label', 'Remove');
      removeBtn.dataset.action = 'click->select#removeExternalItem';
      removeBtn.dataset.itemId = itemId;
      item.appendChild(removeBtn);

      this.itemsContainerTarget.appendChild(item);
    });
  }

  removeExternalItem(event) {
    event.preventDefault();
    const itemId = event.currentTarget.dataset.itemId;
    this.select.removeItem(itemId);
  }

  fetchFromUrl = (search, callback) => {
    let params = new URLSearchParams({
      q: search,
      type: this.recordTypeValue,
      include_archived: "false"
    });

    if (this.companyIdValue && !this.searchGlobal) {
      params.append("company", this.companyIdValue);
    }

    if (this.hasGroupIdValue && this.groupIdValue) {
      params.append("group_id", this.groupIdValue);
    }

    if (this.hasAssetTypeValue && this.assetTypeValue) {
      params.append("asset_type", this.assetTypeValue);
    }

    if (this.hasRecordTypeFilterValue && this.recordTypeFilterValue) {
      params.append("record_type", this.recordTypeFilterValue);
    }

    fetch(`${this.urlValue}?${params.toString()}`)
      .then(response => response.json())
      .then(data => callback(this.prependStaticOptions(this.prependAllOption(data.map(d => this.decorateOption(d))))))
      .catch(() => callback());
  };

  fetchFromOptions = (search, callback) => {
    const filteredOptions = this.optionsValue.filter(option =>
      option.name.toLowerCase().includes(search.toLowerCase())
    ).map(option => this.decorateOption(option));

    callback(this.prependStaticOptions(this.prependAllOption(filteredOptions)));
  }

  prependStaticOptions(options) {
    if (!this.prependOptionsValue || this.prependOptionsValue.length === 0) return options;

    const decorated = this.prependOptionsValue.map(o => this.decorateOption(o));

    return [...decorated, ...options.filter(o => !this.prependIdsSet.has(String(o.id)))];
  }

  prependAllOption(options) {
    if (!this.hasAllOptionValue) return options;

    const allId = this.constructor.allOptionId;
    const allOption = this.decorateOption({
      id: allId,
      name: this.allOptionValue
    });

    return [allOption, ...options.filter(o => String(o.id) !== allId)];
  }

  decorateOption(option) {
    if (!this.hasAllOptionValue && this.prependOptionsValue.length === 0) return option;

    const decorated = { ...option };
    if (this.hasAllOptionValue) {
      decorated.all_option_rank = String(option.id) === this.constructor.allOptionId ? 0 : 1;
    }
    if (this.prependOptionsValue.length > 0) {
      decorated.prepend_rank = this.prependIdsSet.has(String(option.id)) ? 0 : 1;
    }
    return decorated;
  }

  // Replace the option list while preserving any currently-selected items.
  // Used by parent controllers that need to swap the option set in response
  // to external state changes (e.g. picking a new integration repopulates the
  // available connected field data points).
  replaceOptions(options, selectedValue = null) {
    if (!this.select) return;

    this.optionsValue = options;

    this.select.clearOptions();
    this.select.addOptions(options);

    if (selectedValue && !this.select.items.includes(String(selectedValue))) {
      const match = options.find((o) => String(o.id) === String(selectedValue));
      if (match) {
        this.select.addItem(match.id, true);
      }
    }

    this.select.refreshOptions(false);
  }

  submitForm() {
    const form = this.element.closest('form');
    if (form) {
      if (this.select && typeof this.select.sync === 'function') {
        this.select.sync();
      }
      if (this.select) {
        this.select.blur();
      }
      // Give TomSelect a moment to sync its state to the underlying select element
      setTimeout(() => {
        form.requestSubmit();
      }, 0);
    }
  }

  destroyTomSelect() {
    if (this.select) {
      this.select.destroy();
      this.select = null;
    }
  }

  cancelPostTurboClose() {
    if (this.postTurboCloseRaf) {
      cancelAnimationFrame(this.postTurboCloseRaf);
      this.postTurboCloseRaf = null;
    }
  }

  // Ensure the select element has the correct value before form submission
  // TomSelect manages options internally, but we need to ensure the underlying
  // select element has the value set for form submission
  syncSelectValue(value = null) {
    if (this.singleItemValue) {
      const singleValue = Array.isArray(value) ? (value[0] || '') : (value ?? this.select?.items?.[0] ?? '');

      if (singleValue) {
        // Try to set the value directly
        this.selectTarget.value = singleValue;

        // If the option didn't exist, create it and set again
        if (this.selectTarget.value !== singleValue) {
          const option = document.createElement('option');
          option.value = singleValue;
          option.text = singleValue;
          this.selectTarget.appendChild(option);
          this.selectTarget.value = singleValue;
        }
      } else {
        this.selectTarget.value = '';
      }
      return;
    }

    const selectedIds = this.select?.items || [];
    const existingOptions = Array.from(this.selectTarget.options);

    // Clear previous selection first.
    existingOptions.forEach((option) => {
      option.selected = false;
    });

    selectedIds.forEach((id) => {
      let option = existingOptions.find((opt) => String(opt.value) === String(id));
      if (!option) {
        option = document.createElement('option');
        option.value = id;
        option.text = id;
        this.selectTarget.appendChild(option);
      }
      option.selected = true;
    });
  }

  updateAllSelectedState() {
    if (!this.select || !this.itemsOutsideValue) return;
    const total = Object.keys(this.select.options).length;
    const selected = this.select.items.length;
    if (total > 0 && selected >= total) {
      this.select.wrapper.setAttribute('data-all-selected', '');
    } else {
      this.select.wrapper.removeAttribute('data-all-selected');
    }
  }
}
